import { useEffect, useState } from "react";
import { api, ACCOUNT_COLORS, ACCOUNT_LABEL } from "@/lib/api";
import { toast } from "sonner";

const TEMPLATE_OPTIONS = [
  { key: "family1", label: "FAMILY 1" },
  { key: "family2", label: "FAMILY 2" },
  { key: "family3", label: "FAMILY 3" },
];

const ACCOUNT_OPTIONS = ["admin", "member", "child"];

export default function UserEditor({ user, locked, onUserUpdate, onUserDelete }) {
  const [name, setName] = useState(user.name);
  const [accountType, setAccountType] = useState(user.account_type);
  const [pin, setPin] = useState(user.pin || "");
  const [rules, setRules] = useState(["", "", "", "", ""]);
  const [tasksId, setTasksId] = useState(null);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setName(user.name);
    setAccountType(user.account_type);
    setPin(user.pin || "");
  }, [user]);

  useEffect(() => {
    let mounted = true;
    api.getTasks(user.id).then((t) => {
      if (!mounted) return;
      setTasksId(t.id);
      setRules((t.rules || []).concat(["","","","",""]).slice(0,5));
    });
    return () => { mounted = false; };
  }, [user.id]);

  const saveUserDetails = async () => {
    try {
      const payload = { name: name.trim() || user.name, account_type: accountType };
      if (pin && pin.length === 4) payload.pin = pin;
      if (!pin) payload.clear_pin = true;
      const updated = await api.updateUser(user.id, payload);
      onUserUpdate(updated);
      toast.success("Player saved");
      setDirty(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  // PIN-only save: bypasses backend lockout on active leagues so admins
  // can rotate PINs mid-season. Sends ONLY {pin} or {clear_pin}.
  const savePinOnly = async () => {
    try {
      const payload = (pin && pin.length === 4) ? { pin } : { clear_pin: true };
      const updated = await api.updateUser(user.id, payload);
      onUserUpdate(updated);
      toast.success(pin ? `PIN rotated for ${updated.name}` : `PIN cleared for ${updated.name}`);
      setDirty(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "PIN save failed");
    }
  };

  const saveRules = async (next) => {
    try {
      await api.updateTasks(user.id, next);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save rules");
    }
  };

  const setRule = (i, value) => {
    const next = [...rules];
    next[i] = value;
    setRules(next);
  };

  const applyTemplate = async (key) => {
    if (locked) return;
    try {
      const tmpl = await api.getTemplate(key);
      setRules(tmpl.rules);
      await saveRules(tmpl.rules);
      toast.success(`${key.toUpperCase()} template applied to ${user.name}`);
    } catch {
      toast.error("Couldn't load template");
    }
  };

  const onRuleBlur = () => saveRules(rules);

  const color = ACCOUNT_COLORS[accountType] || "#fff";

  return (
    <div
      className="border border-[var(--stakes-border)] bg-[var(--stakes-surface)]"
      data-testid={`user-editor-${user.id}`}
      style={{ boxShadow: `inset 0 0 0 2px ${dirty ? color : "transparent"}` }}
    >
      <div className="p-4 sm:p-5 border-b border-[var(--stakes-border)] flex flex-wrap gap-3 sm:gap-4 items-end">
        <div className="flex-1 min-w-[140px]">
          <div className="label-mono">PLAYER NAME</div>
          <input
            value={name}
            disabled={locked}
            onChange={(e) => { setName(e.target.value); setDirty(true); }}
            onBlur={saveUserDetails}
            className="w-full bg-transparent border-0 border-b-2 border-[var(--stakes-border)] focus:border-white outline-none font-anton text-xl sm:text-2xl uppercase pt-1 pb-1"
            data-testid={`user-name-input-${user.id}`}
          />
        </div>

        <div>
          <div className="label-mono">ROLE</div>
          <div className="flex gap-px mt-1" data-testid={`user-role-toggle-${user.id}`}>
            {ACCOUNT_OPTIONS.map((opt) => (
              <button
                key={opt}
                disabled={locked}
                onClick={() => { setAccountType(opt); setDirty(true); }}
                onBlur={saveUserDetails}
                className={`px-3 h-10 label-mono border ${accountType === opt ? "bg-white text-black border-white" : "border-[var(--stakes-border)] hover:bg-white/10"}`}
                style={accountType === opt ? { borderColor: ACCOUNT_COLORS[opt], background: ACCOUNT_COLORS[opt], color: "#000" } : {}}
              >
                {ACCOUNT_LABEL[opt]}
              </button>
            ))}
          </div>
        </div>

        <div>
          <div className="label-mono" style={{ color: locked ? "var(--neon-blue)" : undefined }}>
            PIN {locked && <span>• ADMIN OVERRIDE</span>}
          </div>
          <div className="flex items-stretch gap-0 mt-1">
            <input
              value={pin}
              type="password"
              disabled={false}
              onChange={(e) => { setPin(e.target.value.replace(/\D/g,"").slice(0,4)); setDirty(true); }}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); savePinOnly(); } }}
              placeholder="—"
              inputMode="numeric"
              autoComplete="new-password"
              className="w-24 bg-transparent border border-[var(--stakes-border)] focus:border-[var(--neon-blue)] outline-none font-mono-st text-xl text-center h-10 tracking-[0.4em]"
              data-testid={`user-pin-input-${user.id}`}
            />
            <button
              type="button"
              onClick={savePinOnly}
              className="h-10 px-3 bg-[var(--neon-blue)] !text-black font-mono uppercase tracking-wider text-xs font-black border border-[var(--neon-blue)] hover:bg-white hover:!text-black hover:border-white hover-flip"
              data-testid={`user-pin-save-${user.id}`}
              title="Save PIN (admin override — works mid-league)"
            >
              SAVE PIN
            </button>
          </div>
        </div>

        {!locked && (
          <button
            onClick={() => {
              if (window.confirm(`Remove ${user.name}?`)) onUserDelete(user.id);
            }}
            className="label-mono h-10 px-3 border border-[var(--stakes-border)] hover:border-[var(--stakes-primary)] hover:text-[var(--stakes-primary)] hover-flip"
            data-testid={`user-delete-button-${user.id}`}
          >
            REMOVE
          </button>
        )}
      </div>

      <div className="p-4 sm:p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="label-mono">5 HABIT RULES {locked && <span style={{color: "var(--stakes-primary)"}}>• LOCKED</span>}</div>
          {!locked && (
            <div className="flex gap-2" data-testid={`templates-${user.id}`}>
              {TEMPLATE_OPTIONS.map((t) => (
                <button
                  key={t.key}
                  onClick={() => applyTemplate(t.key)}
                  className="label-mono px-3 h-8 border border-[var(--stakes-border)] hover:bg-white hover:text-black hover-flip"
                  data-testid={`template-${t.key}-${user.id}`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="space-y-2">
          {rules.map((r, i) => (
            <div key={i} className="flex items-start gap-2 sm:gap-3">
              <div
                className="w-8 h-8 sm:w-9 sm:h-9 flex-shrink-0 flex items-center justify-center font-anton text-base sm:text-xl mt-1"
                style={{ background: color, color: "#000" }}
              >
                {i + 1}
              </div>
              <textarea
                value={r}
                disabled={locked}
                onChange={(e) => setRule(i, e.target.value)}
                onBlur={onRuleBlur}
                rows={1}
                placeholder={locked ? "—" : `Rule ${i + 1}`}
                className="flex-1 min-w-0 bg-transparent border border-[var(--stakes-border)] focus:border-white outline-none px-3 sm:px-4 py-2 font-barlow text-sm sm:text-base uppercase tracking-wide leading-snug resize-none whitespace-normal break-words disabled:opacity-80"
                style={{ minHeight: "44px" }}
                onInput={(e) => {
                  // Auto-grow: reset then snap to content height so long
                  // rules wrap to 2-3 lines instead of scrolling sideways.
                  e.target.style.height = "auto";
                  e.target.style.height = `${e.target.scrollHeight}px`;
                }}
                ref={(el) => {
                  // Initial sizing on mount/render
                  if (el && el.scrollHeight && el.scrollHeight !== el.clientHeight) {
                    el.style.height = "auto";
                    el.style.height = `${el.scrollHeight}px`;
                  }
                }}
                data-testid={`habit-rule-input-${user.id}-${i}`}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
